import * as React from "react";
import {
    DragDropContext,
    DragStart,
    Droppable,
    DropResult
} from "react-beautiful-dnd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { StudyPhase } from "webportalclient";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import {
    DeviceTypes,
    EDiarySessionScheduleItem,
    EDiaryVisitScheduleItem,
    ModelError,
    QueryOrderDirection,
    VisitScheduleItem
} from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useCookieRedirect } from "../../misc/CookieRedirect";
import { Reorder } from "./DragDropHelpers";
import { SessionConfiguration } from "./SessionConfiguration";
import { StudyPhases } from "./StudyPhases";
import { GetReferenceOrStudyPhases, VisitConfiguration } from "./VisitConfiguration";
import { VisitGroup } from "./VisitGroup";
import { useVisitScheduleContext, VisitScheduleContextProvider } from "./VisitScheduleContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { ImportScheduleModal } from "./ImportScheduleModal";
import { CoughProcessingAlgorithmEnum } from "../../misc/api/apiTypes";

export interface SessionScheduleAction {
    id: string;
    actionType: number;
    actionValue: string;
    displayOrder: number;
}

export interface EDiaryScheduleSession {
    resetDiaryOnUpload: boolean;
    includedInDailySummaryCompliance?: boolean;
    expectedMinimumTests?: number;
    expectedMinimumQuestionnaires?: number;
    configuration: string;
}

export interface ScheduleSession {
    id: string;
    label: string;
    rules: string;
    description: string;
    group: string;
    isMandatory: boolean;
    disabled: boolean;
    rolling: boolean;
    continueTest: boolean;
    sessionType: SessionTypes;
    deviceType: DeviceTypes;
    requiredSessions: any[];
    preSessionItemId: string;
    eDiary: EDiaryScheduleSession;
    cohort: string;
    afterRandomisation: boolean;
    requireDoseTime?: boolean;
    tags?: string;
    visualReview?: boolean;
    unscheduled: boolean;
    supportsShadowAssessments: boolean;
    processingAlgorithms: string[];
}

export interface ScheduleVisit {
    id: string;
    label: string;
    description: string;
    canSpanDays: boolean;
    commentRequired: boolean;
    confirmationRequired: boolean;
    obligation: string;
    disabled: boolean;
    phase: string;
    requiredVisits: any[];
    blockedByVisits: any[];
    sessions: ScheduleSession[];
    cohort: string;
}

export interface ActiveItem {
    id: string;
    type: string;
    visitId: string;
}

export interface VisitScheduleDragDropRootProps {
    isVisitActive: boolean;
    schedule: ScheduleVisit[];
    setActiveItem: (item: ActiveItem) => void;
    onAddSession: (visitId: string) => void;
    phases: StudyPhase[];
}

export const GetBinStyle = isDraggingOver => ({
    background: isDraggingOver ? "lightblue" : "#f1f2f2",
    padding: 8,
    height: "10%",
    width: "auto"
});

export const VisitScheduleDragDropRoot = ({
    isVisitActive,
    schedule,
    setActiveItem,
    onAddSession,
    phases,
}: VisitScheduleDragDropRootProps) => {
    const getListStyle = isDraggingOver => ({
        background: isDraggingOver ? "lightblue" : "#f1f2f2",
        padding: 8,
        width: "auto"
    });

    return (
        <>
            <Droppable droppableId="Visit" isDropDisabled={!isVisitActive}>
                {(provided, snapshot) => (
                    <div
                        id="StudyScheduleRoot"
                        className="well"
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver)}
                    >
                        {schedule.map((visit, i) => (
                            <VisitGroup
                                key={`VisitGroup${visit.id}`}
                                visit={visit}
                                index={i}
                                isSessionDropDisabled={isVisitActive}
                                setActive={item => setActiveItem(item)}
                                addSession={() => onAddSession(visit.id)}
                                phases={phases}
                            />
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
            <Droppable droppableId="Delete">
                {(provided, snapshot) => (
                    <div
                        className="well"
                        ref={provided.innerRef}
                        style={GetBinStyle(snapshot.isDraggingOver)}
                    >
                        <FontAwesomeIcon icon={faTrash} size="lg" className="text-danger" />
                        &nbsp; Drag here to remove an item
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </>
    );
};

export const VisitScheduleCore = () => {
    const [activeItem, setActiveItem] = React.useState<ActiveItem>({
        id: null,
        type: null,
        visitId: null
    });

    const [schedule, setSchedule] = React.useState<ScheduleVisit[]>([]);
    const [apiSchedule, setApiSchedule] = React.useState<VisitScheduleItem[]>([]);
    const [importedSchedule, setImportedSchedule] = React.useState<VisitScheduleItem[]>([]);
    const [isVisitActive, setIsVisitActive] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [unsavedChanges, setUnsavedChanges] = React.useState<boolean>(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const [importAuditModalVisible, setImportAuditModalVisible] = React.useState(false);
    const [importScheduleModalVisible, setImportScheduleModalVisible] = React.useState(false);
    const [alertShown, setAlertShown] = React.useState(false);
    const [alert, setAlert] = React.useState<string>();
    const [initialSchedule, setInitialSchedule] = React.useState<ScheduleVisit[]>([]);
    const [phasesModalVisible, setPhasesModalVisible] = React.useState(false);
    const [phases, setPhases] = React.useState<StudyPhase[]>([]);
    const [availableProcessingAlgorithms, setAvailableProcessingAlgorithms] = React.useState<{ id: string; label: string }[]>([]);

    const client = useWebPortalApiClient();
    const { study } = useVisitScheduleContext();
    const legacyClient = useWebPortalLegacyClient();

    const applyScheduleVisitModel = (model: ScheduleVisit[]) => {
        setInitialSchedule(model);
        setSchedule(model);
    }

    React.useEffect(() => {
        if (JSON.stringify(schedule) == JSON.stringify(initialSchedule)) {
            setUnsavedChanges(false);
            return;
        }

        setUnsavedChanges(true);
    }, [schedule, initialSchedule]);

    const getSchedule = async () => {
        const scheduleResult = await legacyClient.getVisitSchedule({
            Filters: [{ Member: "StudyId", Values: [study.id] }],
            Order: { Member: "DisplayOrder", Direction: QueryOrderDirection.Ascending }
        });

        setApiSchedule(scheduleResult.Data);

        // Get available processing algorithms from study settings
        const studySettings = await legacyClient.getStudyCoughSettings(study.id);
        const algorithmNumbers = studySettings.ProcessingAlgorithms as number[];

        const algorithmObjects = algorithmNumbers
            .map(num => {
                const enumKey = CoughProcessingAlgorithmEnum[num] as string;

                return {
                    id: enumKey,
                    label: enumKey
                };
            });

        setAvailableProcessingAlgorithms(algorithmObjects);

        const allSessionsFlattern = scheduleResult.Data.map(v => v.Sessions).flat();

        const mappedSchedule = scheduleResult.Data.map(v => ({
            id: v.Id,
            label: v.Label,
            description: v.Description,
            canSpanDays: v.CanSpanDays,
            commentRequired: v.CommentRequired,
            confirmationRequired: v.ConfirmationRequired,
            obligation: v.Obligation,
            disabled: v.Disabled,
            phase: v.Phase.Id,
            requiredVisits: v.RequiredVisits.map(rv => scheduleResult.Data.find(s => s.Id === rv)),
            blockedByVisits: v.BlockedByVisits.map(bv => scheduleResult.Data.find(s => s.Id === bv)),
            cohort: v.Cohort,
            sessions: v.Sessions.sort((a, b) => a.DisplayOrder - b.DisplayOrder).map(s => ({
                id: s.Id,
                label: s.Label,
                rules: s.Rules,
                description: s.Description,
                isMandatory: s.IsMandatory,
                disabled: s.Disabled,
                rolling: s.Rolling,
                continueTest: s.ContinueTest,
                group: s.Group,
                sessionType: s.SessionTypeId,
                deviceType: s.DeviceTypeId,
                requiredSessions: s.RequiredSessions.map(rs => allSessionsFlattern.find(s => s.Id === rs)),
                preSessionItemId: s.PreSessionItemId,
                cohort: s.Cohort,
                afterRandomisation: s.AfterRandomisation,
                visualReview: s.VisualReview,
                requireDoseTime: s.RequireDoseTime,
                tags: s.Tags,
                unscheduled: s.Unscheduled,
                supportsShadowAssessments: s.SupportsShadowAssessments,
                processingAlgorithms: s.ProcessingAlgorithms ?? [],
                eDiary: {
                    startWindow: s.StartOfWindow,
                    endWindow: s.EndOfWindow,
                    resetDiaryOnUpload: s.ResetDiaryOnUpload,
                    includedInDailySummaryCompliance: s.IncludedInDailySummaryCompliance,
                    configuration: s.Configuration,
                    expectedMinimumQuestionnaires: s.ExpectedMinimumQuestionnaires,
                    expectedMinimumTests: s.ExpectedMinimumTests,
                }
            })),
        }));

        applyScheduleVisitModel(mappedSchedule);
    };

    React.useEffect(() => {
        getSchedule();
    }, []);

    React.useEffect(() => {
        GetReferenceOrStudyPhases(client.client, study, setPhases);
    }, [client.client, study, phasesModalVisible]);

    const onDragStart = (initial: DragStart) => {
        const dropId = initial.source.droppableId;

        if (dropId.includes("Visit")) {
            setIsVisitActive(true);
        } else {
            setIsVisitActive(false);
        }
    };

    const onDragEndInDelete = (result: DropResult) => {
        if (result.source.droppableId.includes("Visit")) {
            const withoutDeleted = schedule.filter(v => v.id !== result.draggableId);

            setSchedule(withoutDeleted);
        } else {
            const withoutDeleted = schedule.find(v => v.id === result.source.droppableId).sessions.filter(v => v.id !== result.draggableId);

            setSchedule(
                schedule.map(v => {
                    if (v.id === result.source.droppableId) {
                        return {
                            ...v,
                            sessions: withoutDeleted,
                        }
                    } else {
                        return v;
                    }
                })
            )
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;

        if (result.destination.droppableId === "Delete") {
            return onDragEndInDelete(result);
        }

        if (result.destination.droppableId.includes("Visit")) {
            const visits = Reorder<ScheduleVisit>(
                schedule,
                result.source.index,
                result.destination.index
            );

            setSchedule(visits);
        } else {
            const sessionMap = schedule.reduce((v, i) => {
                v[i.id] = i.sessions;
                return v;
            }, {});

            const sourceParent = result.source.droppableId;
            const destParent = result.destination.droppableId;

            const sourceItems = sessionMap[sourceParent];
            const destItems = sessionMap[destParent];

            let newItems = [...schedule];

            if (sourceParent === destParent) {
                const orderedSessions = Reorder<ScheduleSession>(
                    sourceItems,
                    sourceIndex,
                    destIndex
                );

                newItems = newItems.map(item => {
                    if (item.id === sourceParent) {
                        item.sessions = orderedSessions;
                    }
                    return item;
                });

                setSchedule(newItems);
            } else {
                const newSourceSubItems = [...sourceItems];
                const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1);

                const newDestSubItems = [...destItems];
                newDestSubItems.splice(destIndex, 0, draggedItem);
                newItems = newItems.map(item => {
                    if (item.id === sourceParent) {
                        item.sessions = newSourceSubItems;
                    } else if (item.id === destParent) {
                        item.sessions = newDestSubItems;
                    }
                    return item;
                });

                setSchedule(newItems);
            }
        }
    };

    const onAddVisit = () => {
        const newVisit = { id: v4() };

        setActiveItem({ id: newVisit.id, type: "Visit", visitId: newVisit.id });
    };

    const onAddSession = (visitId: string) => {
        const newSession = { id: v4(), content: "" };

        setActiveItem({ id: newSession.id, type: "Session", visitId: visitId });
    };

    const onCompleteAddVisit = (visit: ScheduleVisit) => {
        let newSchedule = schedule.map(v => {
            if (v.id === visit.id) {
                return {
                    ...visit,
                    sessions: v.sessions
                };
            } else {
                return v;
            }
        });

        if (newSchedule.find(v => v.id === visit.id) === undefined) {
            newSchedule = [...newSchedule, visit];
        }

        setSchedule(newSchedule);
        setActiveItem({ id: null, type: null, visitId: null });
    };

    const onCompleteAddSession = (visitId: string, session: ScheduleSession) => {
        let newSchedule = schedule.map(v => {
            if (v.id === visitId) {
                return {
                    ...v,
                    sessions: v.sessions.map(s => {
                        if (s.id === session.id) {
                            return session;
                        } else {
                            return s;
                        }
                    })
                };
            } else {
                return v;
            }
        });

        if (
            newSchedule.find(v => v.id === visitId).sessions.find(s => s.id === session.id) ===
            undefined
        ) {
            newSchedule = newSchedule.map(v => {
                if (v.id === visitId) {
                    return {
                        ...v,
                        sessions: [...v.sessions, session]
                    };
                } else {
                    return v;
                }
            });
        }

        setSchedule(newSchedule);
        setActiveItem({ id: null, type: null, visitId: null });
    };

    const onPreAuditSubmit = () => {
        setAuditModalVisible(true);
    }

    const onScheduleSave = async (auditForm: AuditForm) => {
        setAlert(undefined);
        setSubmitting(true);

        const visitIds = schedule.map(v => v.id);
        const sessionIds = schedule.flatMap(v => v.sessions.map(s => s.id));

        const apiModel: EDiaryVisitScheduleItem[] = schedule.map((v, i) => ({
            Id: v.id,
            VisitScheduleId: null,
            Label: v.label,
            Description: v.description,
            Disabled: v.disabled,
            CanSpanDays: v.canSpanDays,
            CommentRequired: v.commentRequired,
            ConfirmationRequired: v.confirmationRequired,
            Obligation: v.obligation,
            Phase: null,
            PhaseId: v.phase,
            StudyId: study.id,
            RequiredVisits: v.requiredVisits ? v.requiredVisits.map(rv => rv.Id) : [],
            BlockedByVisits: v.blockedByVisits ? v.blockedByVisits.map(bv => bv.Id) : [],
            Cohort: v.cohort,
            Sessions: v.sessions.map((s, sI) => ({
                Id: s.id,
                Label: s.label,
                IsMandatory: s.isMandatory,
                Description: s.description,
                Rules: s.rules,
                DeviceType: null,
                DeviceTypeId: s.deviceType as DeviceTypes,
                VisitScheduleItem: null,
                VisitScheduleItemId: v.id,
                SessionTypeId: s.sessionType as SessionTypes,
                StudyId: study.id,
                DisplayOrder: sI,
                Disabled: s.disabled,
                Rolling: s.rolling,
                ContinueTest: s.continueTest,
                Group: s.group,
                StartOfWindow: null,
                EndOfWindow: null,
                ResetDiaryOnUpload: s.eDiary?.resetDiaryOnUpload,
                RequiredSessions: s.requiredSessions ? s.requiredSessions.map(rs => rs.Id) : [],
                PreSessionItemId: s.preSessionItemId,
                Cohort: s.cohort,
                AfterRandomisation: s.afterRandomisation,
                VisualReview: s.visualReview,
                RequireDoseTime: s.requireDoseTime,
                Tags: s.tags,
                Unscheduled: s.unscheduled,
                SupportsShadowAssessments: s.supportsShadowAssessments,
                IncludedInDailySummaryCompliance: s.eDiary?.includedInDailySummaryCompliance,
                Configuration: s.eDiary?.configuration,
                ExpectedMinimumQuestionnaires: s.eDiary?.expectedMinimumQuestionnaires,
                ExpectedMinimumTests: s.eDiary?.expectedMinimumTests,
                ProcessingAlgorithms: s.processingAlgorithms ?? []
            })),
            DisplayOrder: i
        }));


        const visitScheduleResult = await legacyClient.getVisitSchedule({
            Filters: [{ Member: "StudyId", Values: [study.id] }],
            Order: { Member: "DisplayOrder", Direction: QueryOrderDirection.Ascending }
        });

        const sessionScheduleResult = await legacyClient.getSessionSchedule({
            Filters: [{ Member: "StudyId", Values: [study.id] }],
            Order: { Member: "DisplayOrder", Direction: QueryOrderDirection.Ascending }
        });

        for (const v of schedule) {
            v.requiredVisits = v.requiredVisits ? v.requiredVisits.filter(rv => visitIds.includes(rv.Id)).map(rv => visitScheduleResult.Data.find(s => s.Id === rv.Id)) : []

            for (const s of v.sessions) {
                s.requiredSessions = s.requiredSessions ? s.requiredSessions.filter(rs => sessionIds.includes(rs.Id)).map(rs => sessionScheduleResult.Data.find(s => s.Id === rs.Id)) : []
            }
        }

        try {
            const result = await legacyClient.editVisitSchedule(study.id, apiModel, auditForm.Annotation);
            if (result) {
                setAlertShown(true);
                setAlert(undefined);

                applyScheduleVisitModel(schedule);
                await getSchedule();
            }
        }
        catch (err) {
            setAlertShown(undefined);
            setAlert((err as ModelError).Message);
        }

        setAuditModalVisible(false);
        setSubmitting(false);
        window.scroll(0, 0);
    };

    const onVisitClone = () => {
        const currentVisit = schedule.find(v => v.id === activeItem.id);

        onCompleteAddVisit({
            ...currentVisit,
            id: v4(),
            label: `${currentVisit.label}_Copy`,
            sessions: currentVisit.sessions.map(s => ({
                ...s,
                id: v4(),
                label: `${s.label}_Copy`,
                eDiary: {
                    ...s.eDiary,
                }
            }))
        });

        setActiveItem({ id: null, type: null, visitId: null });
    }

    const onSessionClone = () => {
        const currentSession = schedule.find(v => v.id === activeItem.visitId)
            .sessions.find(s => s.id === activeItem.id);

        onCompleteAddSession(activeItem.visitId,
            {
                ...currentSession,
                id: v4(),
                label: `${currentSession.label}_Copy`,
                eDiary: {
                    ...currentSession.eDiary,
                }
            });

        setActiveItem({ id: null, type: null, visitId: null });
    }

    const exportSchedule = () => {
        try {
            setAlertShown(undefined);
            let scheduleClone = JSON.parse(JSON.stringify(apiSchedule)) as VisitScheduleItem[];
            scheduleClone.forEach(vs => {
                delete vs.Phase;
                delete vs.StudyId

                vs.Sessions.forEach(s => {
                    delete s.DeviceType
                    delete s.StudyId
                });
            })
            const jsonString = JSON.stringify(scheduleClone, null, 2); // pretty-print with 2 spaces

            const blob = new Blob([jsonString], { type: "application/json" });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "study-schedule.json";

            link.click();

            URL.revokeObjectURL(link.href);
        } catch (error) {
            setAlert(error);
        }
    }

    const onImportSchedule = (schedule: VisitScheduleItem[]) => {
        try {
            setImportedSchedule(schedule);
            setImportScheduleModalVisible(false);
            setImportAuditModalVisible(true);

        } catch (error) {
            setAlert(error);
        }
    }

    const onImportScheduleSave = async (auditForm: AuditForm) => {
        setAlert(undefined);
        setSubmitting(true);

        let scheduleToImport = null;
        try {
            scheduleToImport = replaceScheduleIds(importedSchedule);
        } catch (err) {
            setAlertShown(undefined);
            setAlert(err);
            return;
        }

        try {
            const result = await legacyClient.editVisitSchedule(study.id, scheduleToImport, auditForm.Annotation);
            if (result) {
                setAlertShown(true);
                setAlert(undefined);
                applyScheduleVisitModel(schedule);
                await getSchedule();
            }
        }
        catch (err) {
            setAlertShown(undefined);
            setAlert((err as ModelError)?.Message ?? err)
        }

        setImportAuditModalVisible(false);
        setSubmitting(false);
    }

    const replaceScheduleIds = (schedule: VisitScheduleItem[]) => {

        let scheduleClone = JSON.parse(JSON.stringify(schedule)) as VisitScheduleItem[];
        let flattenSessions = scheduleClone.flatMap(x => x.Sessions);

        // loop on visits
        scheduleClone.forEach(visit => {
            // generate new visit id
            const visitOldId = visit.Id;
            const visitNewId = v4();

            // replace blocked visits
            scheduleClone.filter(e => e.BlockedByVisits.includes(visitOldId)).forEach(vs => {
                const indexOfBlockedVisit = vs.BlockedByVisits.indexOf(visitOldId);
                vs.BlockedByVisits[indexOfBlockedVisit] = visitNewId;
            });

            // replace required visits
            scheduleClone.filter(e => e.RequiredVisits.includes(visitOldId)).forEach(vs => {
                const indexOfRequiredVisit = vs.RequiredVisits.indexOf(visitOldId);
                vs.RequiredVisits[indexOfRequiredVisit] = visitNewId;
            });

            // loop on visit sessions
            visit.Sessions.forEach(session => {
                // create new session id
                const sessionOldId = session.Id;
                const sessionNewId = v4();

                // replace presession item ids
                flattenSessions.filter(e => e.PreSessionItemId == sessionOldId).forEach(s => {
                    s.PreSessionItemId = sessionNewId;
                });

                // replace required sessions ids
                flattenSessions.filter(e => e.RequiredSessions.includes(sessionOldId)).forEach(s => {
                    const indexOfRequiredSession = s.RequiredSessions.indexOf(sessionOldId);
                    s.RequiredSessions[indexOfRequiredSession] = sessionNewId;
                });

                // assign new session id
                session.Id = sessionNewId;
            });

            // assign new visit id
            visit.Id = visitNewId;
        });

        return scheduleClone;
    }

    const onCloseImportScheduleModal = () => {
        setImportedSchedule([]);
        setImportScheduleModalVisible(false);
    }

    const onCloseAuditModal = () => {
        setAuditModalVisible(false);
        onCloseImportScheduleModal();
    }

    return (
        <>
            <Alert show={alert !== undefined} category={AlertCategoryEnum.Danger}>
                {alert}
            </Alert>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to="/studies">Studies list</Link>?
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">
                        Visit Schedule&nbsp;
                        <small>{study.name}</small>
                    </h2>
                </div>
                <div className="col-md-6 h2">
                    <div className="pull-right">
                        <button id="AddVisitButton" className="btn btn-primary" onClick={() => { setAlertShown(undefined); onAddVisit(); }}>
                            Add Visit
                        </button>&nbsp;
                        <button className="btn btn-primary btn-outline" onClick={() => { setAlertShown(undefined); setPhasesModalVisible(true); }}>
                            Study Phases
                        </button>&nbsp;
                        <button className="btn btn-primary btn-outline" onClick={exportSchedule} disabled={apiSchedule?.length == 0}>
                            Export Schedule
                        </button>&nbsp;
                        <button className="btn btn-primary btn-outline" onClick={() => { setAlertShown(undefined); setImportScheduleModalVisible(true) }} disabled={apiSchedule?.length > 0}>
                            Import Schedule
                        </button>&nbsp;
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                        <VisitScheduleDragDropRoot
                            isVisitActive={isVisitActive}
                            schedule={schedule}
                            setActiveItem={item => { setAlertShown(undefined); setActiveItem(item); }}
                            onAddSession={id => { setAlertShown(undefined); onAddSession(id) }}
                            phases={phases}
                        />
                    </DragDropContext>
                </div>
            </div>
            <div className="row pull-right">
                <div className="col-md-12">
                    <button id="SaveButton" className="btn btn-primary" onClick={() => { setAlertShown(undefined); onPreAuditSubmit(); }}>
                        Save
                    </button>&nbsp;
                    <Link className="btn btn-primary btn-outline" to="/studies" data-automationid="ReturnToList">
                        Return to List
                    </Link>
                </div>
            </div>
            {activeItem.id && activeItem.type === "Session" ? (
                <SessionConfiguration
                    study={study}
                    possibleRequiredSessions={schedule.map(v => ({
                        label: v.label,
                        options: v.sessions
                            .filter(s => s.id !== activeItem.id)
                            .map(s => ({ Id: s.id, Label: s.label }))
                    }))}
                    possiblePreSessions={schedule.map(v => (
                        v.sessions
                            .filter(s => s.id !== activeItem.id)
                            .map(s => ({ Id: s.id, Label: s.label }))
                    )).flat()}
                    possibleProcessingAlgorithms={availableProcessingAlgorithms}
                    sessionItem={schedule
                        .find(v => v.id === activeItem.visitId)
                        .sessions.find(s => s.id === activeItem.id)}
                    onComplete={session => {
                        onCompleteAddSession(activeItem.visitId, session);
                    }}
                    onClose={() => setActiveItem({ id: null, type: null, visitId: null })}
                    onClone={onSessionClone}
                />
            ) : null}
            {activeItem.id && activeItem.type === "Visit" ? (
                <VisitConfiguration
                    visitItem={schedule.find(v => v.id === activeItem.id)}
                    possibleRequiredVisits={schedule
                        .filter(s => s.id !== activeItem.id)
                        .map(s => ({ Id: s.id, Label: s.label }))
                    }
                    onComplete={visit => {
                        onCompleteAddVisit(visit);
                        setActiveItem({ id: null, type: null, visitId: null });
                    }}
                    onClose={() => setActiveItem({ id: null, type: null, visitId: null })}
                    onClone={onVisitClone}
                />
            ) : null}

            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={onCloseAuditModal}
                    onSubmit={(audit) => onScheduleSave(audit)}
                />) : null}
            {importAuditModalVisible ? (
                <AuditModal
                    header="Import Schedule Approval"
                    isSubmitting={submitting}
                    onClose={onCloseAuditModal}
                    onSubmit={(audit) => onImportScheduleSave(audit)}
                />) : null}
            {!importScheduleModalVisible ? null :
                <ImportScheduleModal onClose={onCloseImportScheduleModal} onSave={onImportSchedule} />
            }
            {phasesModalVisible ? <StudyPhases onClose={() => setPhasesModalVisible(false)} /> : null}
        </>
    );
};

export const VisitSchedule = () => {
    const { studyId } = useParams<{ studyId: string }>();
    const { study } = useCurrentStudyConfigurationContext();

    useCookieRedirect(studyId);

    if (!study) {
        return <LoadingWithMessage message="Loading Visit Schedule" />;
    }

    return (
        <VisitScheduleContextProvider study={study}>
            <VisitScheduleCore />
        </VisitScheduleContextProvider>
    );
}
